/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/**
 * Defines reusable KPI status rules: thresholds, executable calculations,
 * displayable calculation code, source sensor types, and traffic-light mapping.
 */

/* Import configuration thresholds */
import {
    BL_COST_LAST_CRITICAL,
    BL_COST_LAST_WARNING,
    HE_WEEKEND_REDUCTION_CRITICAL,
    HE_WEEKEND_REDUCTION_WARNING,
    PV_RATIO_CRITICAL,
    PV_RATIO_IMPLAUSIBLE_LOWER,
    PV_RATIO_IMPLAUSIBLE_UPPER,
    PV_RATIO_WARNING,
} from '@/config/app';

/* Import types */
import type { SystemDashboardKpiStatusDefinition } from '@/lib/overview/systemDashboardKpiStatus';

/**
 * IMPORTANT PLACE: Base-load daily cost KPI calculations.
 */
export const BASE_LOAD_COST_DAY_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    calc: ({ values }) => ({
        status: (values.CostDay ?? 0) >= BL_COST_LAST_CRITICAL
            ? 2
            : (values.CostDay ?? 0) >= BL_COST_LAST_WARNING
                ? 1
                : 0,
    }),
    calcCode: `({ values }) => ({
    status: (values.CostDay ?? 0) >= ${BL_COST_LAST_CRITICAL}
        ? 2
        : (values.CostDay ?? 0) >= ${BL_COST_LAST_WARNING}
            ? 1
            : 0,
})`,
    sourceSensorTypes: ['CostDay'],
    trafficLight: {
        rules: [
            { when: 'gte', value: 2, severity: 'danger', title: 'status = 2' },
            { when: 'gte', value: 1, severity: 'warning', title: 'status = 1' },
            { when: 'gte', value: 0, severity: 'success', title: 'status = 0' },
            { when: 'lt', value: 0, severity: 'implausible', title: 'status < 0' },
        ],
        fallback: 'implausible',
    },
};

/**
 * IMPORTANT PLACE: Normalizes the base-load severity sensor value for the shared status UI.
 */
export const BASE_LOAD_SEVERITY_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    calc: ({ values }) => ({
        status: values.Severity === 0 || values.Severity === 100
            ? 0
            : values.Severity === 201
                ? 1
                : values.Severity === 202
                    ? 2
                    : -1,
    }),
    calcCode: `({ values }) => ({
    status: values.Severity === 0 || values.Severity === 100
        ? 0
        : values.Severity === 201
            ? 1
            : values.Severity === 202
                ? 2
                : -1,
})`,
    sourceSensorTypes: ['Severity'],
    trafficLight: {
        rules: [
            { when: 'gte', value: 2, severity: 'danger', title: 'status = 2' },
            { when: 'gte', value: 1, severity: 'warning', title: 'status = 1' },
            { when: 'gte', value: 0, severity: 'success', title: 'status = 0' },
            { when: 'lt', value: 0, severity: 'implausible', title: 'status < 0' },
        ],
        fallback: 'implausible',
    },
};

export const FIXED_OK_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    calc: () => ({ status: 0 }),
    calcCode: `() => ({
    status: 0,
})`,
    sourceSensorTypes: [],
    trafficLight: {
        rules: [
            { when: 'gte', value: 2, severity: 'danger', title: 'status = 2' },
            { when: 'gte', value: 1, severity: 'warning', title: 'status = 1' },
            { when: 'gte', value: 0, severity: 'success', title: 'status = 0' },
            { when: 'lt', value: 0, severity: 'implausible', title: 'status < 0' },
        ],
        fallback: 'implausible',
    },
};

/**
 * IMPORTANT PLACE: Heating energy KPI calculations.
 */
export const HEATING_ENERGY_WEEKEND_REDUCTION_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    calc: ({ values }) => ({
        status: (values.WeekendReductionPercent ?? 0) < HE_WEEKEND_REDUCTION_CRITICAL
            ? 2
            : (values.WeekendReductionPercent ?? 0) < HE_WEEKEND_REDUCTION_WARNING
                ? 1
                : 0,
    }),
    calcCode: `({ values }) => ({
    status: (values.WeekendReductionPercent ?? 0) < ${HE_WEEKEND_REDUCTION_CRITICAL}
        ? 2
        : (values.WeekendReductionPercent ?? 0) < ${HE_WEEKEND_REDUCTION_WARNING}
            ? 1
            : 0,
})`,
    sourceSensorTypes: ['WeekendReductionPercent'],
    trafficLight: {
        rules: [
            { when: 'gte', value: 2, severity: 'danger', title: 'status = 2' },
            { when: 'gte', value: 1, severity: 'warning', title: 'status = 1' },
            { when: 'gte', value: 0, severity: 'success', title: 'status = 0' },
            { when: 'lt', value: 0, severity: 'implausible', title: 'status < 0' },
        ],
        fallback: 'implausible',
    },
};

/**
 * IMPORTANT PLACE: PV KPI calculations.
 */
export const PV_DTC_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    calc: ({ values }) => ({
        status: (values.dtc ?? 0) >= 200
            ? 2
            : (values.dtc ?? 0) >= 100
                ? 1
                : 0,
    }),
    calcCode: `({ values }) => ({
    status: (values.dtc ?? 0) >= 200
        ? 2
        : (values.dtc ?? 0) >= 100
            ? 1
            : 0,
})`,
    sourceSensorTypes: ['dtc'],
    trafficLight: {
        rules: [
            { when: 'gte', value: 2, severity: 'danger', title: 'status = 2' },
            { when: 'gte', value: 1, severity: 'warning', title: 'status = 1' },
            { when: 'gte', value: 0, severity: 'success', title: 'status = 0' },
            { when: 'lt', value: 0, severity: 'implausible', title: 'status < 0' },
        ],
        fallback: 'implausible',
        labels: {
            danger: 'Error',
            warning: 'Warning',
        },
    },
};

/**
 * IMPORTANT PLACE: Water-loss KPI calculations.
 */
export const WATER_LOSS_DTC_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    calc: ({ values }) => ({
        status: (values.dtc_7d ?? 0) >= 200
            ? 2
            : (values.dtc_7d ?? 0) >= 100
                ? 1
                : 0,
    }),
    calcCode: `({ values }) => ({
    status: (values.dtc_7d ?? 0) >= 200
        ? 2
        : (values.dtc_7d ?? 0) >= 100
            ? 1
            : 0,
})`,
    sourceSensorTypes: ['dtc_7d'],
    trafficLight: {
        rules: [
            { when: 'gte', value: 2, severity: 'danger', title: 'status = 2' },
            { when: 'gte', value: 1, severity: 'warning', title: 'status = 1' },
            { when: 'gte', value: 0, severity: 'success', title: 'status = 0' },
            { when: 'lt', value: 0, severity: 'implausible', title: 'status < 0' },
        ],
        fallback: 'implausible',
    },
};

/** Applies the water-loss thresholds to the current daily DTC value. */
export const WATER_LOSS_DAILY_DTC_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    calc: ({ values }) => ({
        status: (values.dtc ?? 0) >= 200
            ? 2
            : (values.dtc ?? 0) >= 100
                ? 1
                : 0,
    }),
    calcCode: `({ values }) => ({
    status: (values.dtc ?? 0) >= 200
        ? 2
        : (values.dtc ?? 0) >= 100
            ? 1
            : 0,
})`,
    sourceSensorTypes: ['dtc'],
    trafficLight: {
        rules: [
            { when: 'gte', value: 2, severity: 'danger', title: 'status = 2' },
            { when: 'gte', value: 1, severity: 'warning', title: 'status = 1' },
            { when: 'gte', value: 0, severity: 'success', title: 'status = 0' },
            { when: 'lt', value: 0, severity: 'implausible', title: 'status < 0' },
        ],
        fallback: 'implausible',
    },
};

/** Combines the latest loss and data-quality codes for system overview cards. */
export const WATER_OVERVIEW_DTC_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    ...WATER_LOSS_DAILY_DTC_STATUS_DEFINITION,
    calc: ({ values }) => {
        const dtc = Math.max(values.dtc ?? 0, values.dtc_off ?? 0);
        return { status: dtc >= 200 ? 2 : dtc >= 100 ? 1 : 0 };
    },
    calcCode: `({ values }) => {
    // Latest water-loss and data-quality DTC; the higher code determines the status.
    const dtc = Math.max(values.dtc ?? 0, values.dtc_off ?? 0);
    return { status: dtc >= 200 ? 2 : dtc >= 100 ? 1 : 0 };
}`,
    sourceSensorTypes: ['dtc', 'dtc_off'],
    trafficLight: {
        ...WATER_LOSS_DAILY_DTC_STATUS_DEFINITION.trafficLight,
        rules: [
            { when: 'gte', value: 2, severity: 'danger', title: 'max(dtc, dtc_off) >= 200' },
            { when: 'gte', value: 1, severity: 'warning', title: '100 <= max(dtc, dtc_off) < 200' },
            { when: 'gte', value: 0, severity: 'success', title: 'max(dtc, dtc_off) < 100' },
        ],
    },
};

/** Evaluates the last water data-quality DTC, independently of the water-loss DTC. */
export const WATER_DATA_QUALITY_DTC_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    ...WATER_LOSS_DAILY_DTC_STATUS_DEFINITION,
    calc: ({ values }) => ({
        status: (values.dtc_off ?? 0) >= 200 ? 2 : (values.dtc_off ?? 0) >= 100 ? 1 : 0,
    }),
    calcCode: `({ values }) => ({
    // Latest dtc_off value, not a maximum over a historical time range.
    status: (values.dtc_off ?? 0) >= 200 ? 2 : (values.dtc_off ?? 0) >= 100 ? 1 : 0,
})`,
    sourceSensorTypes: ['dtc_off'],
};

/**
 * IMPORTANT PLACE: Calculates the PV performance reduction status from the actual-to-expected ratio.
 * IMPORTANT PLACE: Ratios at or above the warning threshold do not represent a relevant reduction.
 */
export const PV_PERFORMANCE_REDUCTION_STATUS_DEFINITION: SystemDashboardKpiStatusDefinition = {
    calc: ({ values }) => ({
        status: values.ratio_30 === null
            || values.ratio_30 < PV_RATIO_IMPLAUSIBLE_LOWER
            || values.ratio_30 > PV_RATIO_IMPLAUSIBLE_UPPER
            ? -1
            : values.ratio_30 >= PV_RATIO_WARNING
                ? 0
                : values.ratio_30 >= PV_RATIO_CRITICAL
                    ? 1
                    : 2,
    }),
    calcCode: `({ values }) => ({
    status: values.ratio_30 === null
        || values.ratio_30 < ${PV_RATIO_IMPLAUSIBLE_LOWER}
        || values.ratio_30 > ${PV_RATIO_IMPLAUSIBLE_UPPER}
        ? -1
        : values.ratio_30 >= ${PV_RATIO_WARNING}
            ? 0
            : values.ratio_30 >= ${PV_RATIO_CRITICAL}
                ? 1
                : 2,
})`,
    sourceSensorTypes: ['ratio_30'],
    trafficLight: {
        rules: [
            { when: 'gte', value: 2, severity: 'danger', title: 'status = 2' },
            { when: 'gte', value: 1, severity: 'warning', title: 'status = 1' },
            { when: 'gte', value: 0, severity: 'success', title: 'status = 0' },
            { when: 'lt', value: 0, severity: 'implausible', title: 'status < 0' },
        ],
        fallback: 'implausible',
        labels: {
            danger: 'General Error',
            warning: 'Performance Deviation',
        }
    },
};
